import { ReturnAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import { Autocomplete } from "@redotech/redo-web/autocomplete";
import { BubbleState, Bubbles } from "@redotech/redo-web/bubbles";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect, useState } from "react";
import * as variantExchange from "./variant-exchange.module.css";

export const isVariantInStock = (
  variant: ExchangeProductVariant,
  settings: ReturnAppSettings,
  ignoreStock?: boolean,
) => {
  if (ignoreStock) {
    return true;
  }

  return (
    continueIfOutOfStock(variant, settings) ||
    (variant.quantityAvaliable || 0) >= settings.inventory.minimum_stock
  );
};

export const continueIfOutOfStock = (
  variant: ExchangeProductVariant,
  settings: ReturnAppSettings,
) => {
  return (
    settings.inventory.followShopifyInventoryPolicy && variant.availableForSale
  );
};

export type ExchangeProductVariant = {
  title: string;
  productTitle: string;
  productId: string;
  id: string;
  price: string;
  option1: string | null;
  option2: string | null;
  option3: string | null;
  requiresShipping: boolean;
  quantityAvaliable: number;
  currentlyNotInStock: boolean;
  selectedOptions: { name: string; value: string }[];
  image: string | null;
  sku: string | null;
  availableForSale: boolean;
};

export type ExhangeProductOptions = {
  id: string;
  name: string;
  values: string[];
};

export type ExchangeProduct = {
  id: string;
  availableForSale: boolean;
  title: string;
  vendor: string;
  productType: string;
  totalInventory: number;
  tags: string[];
  image: string;
  variants: ExchangeProductVariant[];
  options: ExhangeProductOptions[];
  publishedAt: string | null;
  price: string;
  description: string;
  collectionIds: string[];
};

export const VariantBubbles = memo(function VariantBubbles({
  productData,
  setSelectedVariant,
  settings,
  showErrors,
  disabled = false,
  ignoreStock = false,
  ...props
}: {
  productData: ExchangeProduct;
  setSelectedVariant: React.Dispatch<ExchangeProductVariant>;
  settings: ReturnAppSettings;
  showErrors: boolean;
  disabled?: boolean;
  ignoreStock?: boolean;
} & React.HTMLAttributes<HTMLDivElement>) {
  const [option1, setOption1] = useState<string | null>(
    productData?.options?.[0]?.values?.length === 1
      ? productData.options[0].values[0]
      : null,
  );
  const [option2, setOption2] = useState<string | null>(
    productData?.options?.[1]?.values?.length === 1
      ? productData.options[1].values[0]
      : null,
  );
  const [option3, setOption3] = useState<string | null>(
    productData?.options?.[2]?.values?.length === 1
      ? productData.options[2].values[0]
      : null,
  );

  useEffect(() => {
    // When options are updated try to find a variant that matches
    const variant = productData.variants.find(
      (variant) =>
        variant.option1 == option1 &&
        variant.option2 == option2 &&
        variant.option3 == option3,
    );
    if (variant) {
      setSelectedVariant(variant);
    }
  }, [option1, option2, option3, setSelectedVariant, productData]);

  const inStock = (variant: ExchangeProductVariant) => {
    return isVariantInStock(variant, settings, ignoreStock);
  };

  const findRandomValidProduct = (index: number, value: string) => {
    let validProduct: ExchangeProductVariant | undefined;
    if (index === 0) {
      validProduct = productData.variants.find(
        (variant) => variant.option1 === value && inStock(variant),
      );
    } else if (index === 1) {
      validProduct = productData.variants.find(
        (variant) => variant.option2 === value && inStock(variant),
      );
    } else {
      validProduct = productData.variants.find(
        (variant) => variant.option3 === value && inStock(variant),
      );
    }

    if (!validProduct) {
      return;
    }

    setOption1(validProduct.option1);
    setOption2(validProduct.option2);
    setOption3(validProduct.option3);
  };

  const handleClick = (index: number, value: string | null) => {
    if (value && isFaded(index, value)) {
      // todo Select a random valid product
      findRandomValidProduct(index, value);
    } else {
      if (index === 0) {
        setOption1(value);
      } else if (index === 1) {
        setOption2(value);
      } else {
        setOption3(value);
      }
    }
  };

  const isDisabled = (index: number, value: string | null) => {
    // If variant is out of stock for all possibilities disable button
    if (value === null) {
      return true;
    }
    let variants = [];
    if (index === 0) {
      variants = productData.variants.filter(
        (variant) => value === variant.option1,
      );
    } else if (index === 1) {
      variants = productData.variants.filter(
        (variant) => value === variant.option2,
      );
    } else {
      variants = productData.variants.filter(
        (variant) => value === variant.option3,
      );
    }
    return !variants.find((variant) =>
      isVariantInStock(variant, settings, ignoreStock),
    );
  };

  const isFaded = (index: number, value: string | null) => {
    // If variant is out of stock for current selection fade button
    if (value === null) {
      return true;
    }
    let variants: ExchangeProductVariant[] = [];
    if (index === 0) {
      variants = productData.variants.filter(
        (variant) =>
          value === variant.option1 &&
          (!option2 ? true : option2 === variant.option2) &&
          (!option3 ? true : option3 === variant.option3) &&
          inStock(variant),
      );
    }
    if (index === 1) {
      variants = productData.variants.filter(
        (variant) =>
          value === variant.option2 &&
          (!option1 ? true : option1 === variant.option1) &&
          (!option3 ? true : option3 === variant.option3) &&
          inStock(variant),
      );
    }
    if (index === 2) {
      variants = productData.variants.filter(
        (variant) =>
          value === variant.option3 &&
          (!option2 ? true : option2 === variant.option2) &&
          (!option1 ? true : option1 === variant.option1) &&
          inStock(variant),
      );
    }
    return !(variants && variants.length);
  };

  const selected = (index: number) => {
    return [option1, option2, option3][index];
  };

  return (
    <div {...props}>
      {productData &&
        productData.options &&
        productData.options.map((option, index) => (
          <div key={index}>
            <div className={variantExchange.variantOptionName}>
              {option.name} <span className={variantExchange.errorText}>*</span>
            </div>
            {(settings.theme.collapseVariantThreshold ?? 12) <=
            option.values.length ? (
              <Autocomplete
                disableOption={(value) => isDisabled(index, value)}
                getLabel={(value) => value}
                options={option.values}
                value={selected(index)}
                valueChange={(value) => handleClick(index, value)}
              >
                {(value) =>
                  isFaded(index, value) ? (
                    <Text textDecoration="line-through">{value}</Text>
                  ) : (
                    <Text>{value}</Text>
                  )
                }
              </Autocomplete>
            ) : (
              <Bubbles
                onChange={(value) => handleClick(index, value)}
                options={option.values}
                state={(value) =>
                  isDisabled(index, value) || disabled
                    ? BubbleState.DISABLED
                    : isFaded(index, value)
                      ? BubbleState.FADED
                      : BubbleState.DEFAULT
                }
                value={selected(index)}
              />
            )}
            {showErrors && !selected(index) && (
              <div className={variantExchange.errorText}>
                Please make a selection
              </div>
            )}
          </div>
        ))}
    </div>
  );
});
